import { enqueueSnackbar } from 'notistack';
import { ERROR_401 } from 'common/config/api';
import { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_MESSAGE_TIMEOUT, SIGN_IN_VIEW } from 'common/constants/auth';
import {
  buildSignInUrl,
  clearOtpVerified,
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredAccessToken,
} from 'pages/Auth/utils';
import AuthService from './auth';
import BaseService from './base';

type AuthErrorLike = {
  status?: number;
};

const isAuthError = (error: unknown): error is AuthErrorLike =>
  typeof error === 'object' && error !== null && 'status' in error;

const handleExpiredSession = () => {
  clearStoredAuthTokens();
  clearOtpVerified();
  enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
    variant: 'info',
  });
  // Prevent any further API requests
  setTimeout(() => {
    window.location.href = buildSignInUrl({
      returnTo: `${window.location.pathname}${window.location.search}`,
      view: SIGN_IN_VIEW.IDENTIFIER,
    });
  }, SESSION_EXPIRED_MESSAGE_TIMEOUT);
};

const refreshAccessToken = async (): Promise<boolean> => {
  const authService = AuthService.getInstance();
  const accessToken = getStoredAccessToken();
  const refreshToken = getStoredRefreshToken();

  if (!refreshToken) {
    if (accessToken) {
      handleExpiredSession();
    }
    return false;
  }

  const payload = {
    refresh: refreshToken,
  };

  try {
    const newAccessToken = await authService.refreshAccessToken(JSON.stringify(payload));

    if (!newAccessToken?.access) {
      handleExpiredSession();
      return false;
    }

    setStoredAccessToken(newAccessToken.access);
    return true;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error && error.status === Number(ERROR_401)) {
      handleExpiredSession();
      return false;
    }
  }

  return false;
};

const refreshPassThrough = async <TResponse>(next: () => Promise<TResponse>): Promise<TResponse> => {
  try {
    return await next();
  } catch (error) {
    if (isAuthError(error) && error.status === 401) {
      try {
        const didRefresh = await refreshAccessToken();
        if (didRefresh) {
          return await next();
        }
        throw error;
      } catch {
        throw error;
      }
    }
    throw error;
  }
};

class BaseServiceRetryWrapper extends BaseService {
  async get<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(() => super.get<TResponse>(url, headers));
  }

  async getFile<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.getFile<TResponse, TPayload>(url, data, headers));
  }

  async put<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.put<TResponse, TPayload>(url, data, headers));
  }

  async post<TResponse = unknown, TPayload = unknown>(
    url: string,
    data?: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.post<TResponse, TPayload>(url, data, headers));
  }

  async delete<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(() => super.delete<TResponse>(url, headers));
  }

  async patch<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(() => super.patch<TResponse, TPayload>(url, data, headers));
  }
}

export default BaseServiceRetryWrapper;
